import { useEffect, useRef, type Dispatch } from "react";
import type { User } from "oidc-client-ts";
import { useBlocker } from "react-router";
import i18n from "@/i18n";

import { revokePartitionDownloads } from "./externalRegisterImportCsv";
import {
  EXTERNAL_REGISTER_IMPORT_IDLE_TIMEOUT_MS,
  sessionDraftAdapter,
} from "./externalRegisterImportDraft";
import type {
  ExternalRegisterImportAction,
  ExternalRegisterImportState,
} from "./externalRegisterImportReducer";
import { isExternalRegisterImportFlowPath } from "../externalRegisterRoutes";

type ExternalRegisterImportAuthState = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user?: User | null;
};

export const ExternalRegisterImportEffects = ({
  auth,
  dispatch,
  draftIsActive,
  enableNavigationBlocker,
  state,
}: {
  auth: ExternalRegisterImportAuthState;
  dispatch: Dispatch<ExternalRegisterImportAction>;
  draftIsActive: boolean;
  enableNavigationBlocker: boolean;
  state: ExternalRegisterImportState;
}) => {
  const activeDraftRef = useRef(false);
  const partitionDownloadsRef = useRef(state.partitionDownloads);
  const hadAuthenticatedUserRef = useRef(Boolean(auth.user || auth.isAuthenticated));

  activeDraftRef.current = draftIsActive;

  useEffect(() => {
    partitionDownloadsRef.current = state.partitionDownloads;
  }, [state.partitionDownloads]);

  useEffect(() => {
    return () => {
      revokePartitionDownloads(partitionDownloadsRef.current);
    };
  }, []);

  useEffect(() => {
    if (!draftIsActive) return;
    sessionDraftAdapter.save({
      clientImportId: state.clientImportId,
      csvText: state.csvText,
      sourceFileName: state.sourceFileName,
      sourceFileSize: state.sourceFileSize,
      hasHeader: state.hasHeader,
      mapping: state.mapping,
      parseResponse: state.parseResponse,
      validateResponse: state.validateResponse,
      approvedRowIds: Array.from(state.approvedRowIds),
      excludedRowIds: Array.from(state.excludedRowIds),
      selectedPatientIds: state.selectedPatientIds,
      previewRows: state.previewRows,
    });
  }, [
    draftIsActive,
    state.approvedRowIds,
    state.clientImportId,
    state.csvText,
    state.excludedRowIds,
    state.hasHeader,
    state.mapping,
    state.parseResponse,
    state.previewRows,
    state.selectedPatientIds,
    state.sourceFileName,
    state.sourceFileSize,
    state.validateResponse,
  ]);

  useEffect(() => {
    if (!draftIsActive) return;
    const timeout = window.setTimeout(() => {
      sessionDraftAdapter.clear();
      revokePartitionDownloads(state.partitionDownloads);
      dispatch({ type: "reset" });
      dispatch({ type: "setErrorMessage", errorMessage: i18n.t("externalRegister.import.effects.draftCleared") });
    }, EXTERNAL_REGISTER_IMPORT_IDLE_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [
    dispatch,
    draftIsActive,
    state.approvedRowIds,
    state.csvText,
    state.excludedRowIds,
    state.mapping,
    state.partitionDownloads,
    state.selectedPatientIds,
  ]);

  useEffect(() => {
    if (auth.isLoading) return;
    if (auth.user || auth.isAuthenticated) {
      hadAuthenticatedUserRef.current = true;
    }
    if (draftIsActive && hadAuthenticatedUserRef.current && !auth.isAuthenticated) {
      sessionDraftAdapter.clear();
      revokePartitionDownloads(state.partitionDownloads);
      dispatch({ type: "reset" });
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.user, dispatch, draftIsActive, state.partitionDownloads]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!activeDraftRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  return (
    <>
      {enableNavigationBlocker && draftIsActive && (
        <ConfirmedNavigationDraftClearer onConfirmClear={() => sessionDraftAdapter.clear()} />
      )}
    </>
  );
};

const ConfirmedNavigationDraftClearer = ({ onConfirmClear }: { onConfirmClear: () => void }) => {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname === nextLocation.pathname) return false;
    return !isExternalRegisterImportFlowPath(nextLocation.pathname);
  });

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    const confirmed = window.confirm(i18n.t("externalRegister.import.effects.confirmLeave"));
    if (confirmed) {
      onConfirmClear();
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker, onConfirmClear]);

  return null;
};
